import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const FILTERS = ["All", "Website", "E-Commerce", "Software", "Automation", "Business Systems"];

export default function OurWork() {
  const [tab, setTab] = useState("All");
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/portfolio").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const products = items.filter((i) => i.kind === "product");
  const clients = items.filter((i) => i.kind === "client");
  const filterFn = (arr) => tab === "All" ? arr : arr.filter((i) => i.category === tab);

  return (
    <section id="work" className="relative section-pad overflow-hidden" data-testid="work-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-4">05 · Selected Work</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1]">
              Products we've built.<br /><span className="italic">Businesses we've launched.</span>
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-10">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setTab(f)}
              className={`h-9 px-4 rounded-full text-[12.5px] font-medium border transition-colors ${
                tab === f
                  ? "bg-[color:var(--text)] text-[color:var(--bg)] border-[color:var(--text)]"
                  : "border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)]"
              }`}
              data-testid={`work-filter-${f.toLowerCase().replace(/\s+/g,'-')}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Products */}
        <div className="mb-16">
          <div className="overline mb-6">Products We've Built</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {filterFn(products).map((p, i) => (
              <ProjectCard key={p.id} p={p} i={i} spotlightHue={i * 60} />
            ))}
          </div>
        </div>

        {/* Client stories */}
        <div>
          <div className="overline mb-6">Client Success Stories</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filterFn(clients).map((p, i) => (
              <ProjectCard key={p.id} p={p} i={i} big />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ p, i, big = false, spotlightHue = 210 }) {
  return (
    <Reveal delay={i * 0.06}>
      <Link to="/work" className="block group card-surface overflow-hidden relative" data-testid={`project-card-${p.title.toLowerCase()}`}>
        <div className={`relative ${big ? "aspect-[16/10]" : "aspect-[4/3]"} overflow-hidden`}>
          {/* Spotlight */}
          <div className="absolute inset-0 z-10 opacity-70 mix-blend-plus-lighter pointer-events-none"
               style={{ background: `radial-gradient(circle at 30% 40%, hsla(${spotlightHue},80%,55%,0.35), transparent 60%)` }} />
          <motion.img
            src={p.cover_image}
            alt={p.title}
            className="absolute inset-4 object-cover w-[calc(100%-2rem)] h-[calc(100%-2rem)] rounded-xl"
            initial={{ scale: 1.02 }}
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <div className="p-6 flex items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="overline">{p.category}</span>
              {p.industry && <><span className="text-[color:var(--text-muted)]">·</span><span className="overline">{p.industry}</span></>}
            </div>
            <h3 className="font-serif text-2xl md:text-[26px] tracking-tight">{p.title}</h3>
            <p className="text-[13.5px] text-[color:var(--text-muted)] mt-1">{p.subtitle}</p>
          </div>
          <div className="w-10 h-10 rounded-full grid place-items-center border border-[color:var(--border-strong)] transition-transform group-hover:rotate-45">
            <ArrowUpRight size={16} />
          </div>
        </div>
      </Link>
    </Reveal>
  );
}
